import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const authFetch = async (url, logoutFn, options = {}) => {
  const token = localStorage.getItem('admin_token');
  const response = await fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options.headers }
  });
  if (response.status === 401) {
    logoutFn();
    throw new Error('Session expired');
  }
  return response;
};

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'safety_emergency', label: 'Safety/Emergency' },
  { value: 'bad_actor', label: 'Bad Actor' },
  { value: 'payment_dispute', label: 'Payment Dispute' },
  { value: 'bug_report', label: 'Bug Report' },
  { value: 'lost_and_found', label: 'Lost & Found' },
  { value: 'general_feedback', label: 'General Feedback' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const STATUS_STYLES = {
  open: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-600',
};

const PRIORITY_STYLES = {
  critical: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-gray-100 text-gray-600',
};

const CATEGORY_LABELS = {
  safety_emergency: 'Safety/Emergency',
  bug_report: 'Bug Report',
  bad_actor: 'Bad Actor',
  payment_dispute: 'Payment Dispute',
  lost_and_found: 'Lost & Found',
  general_feedback: 'Feedback',
};

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const ReportsTab = () => {
  const { logout } = useAuth();
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Filters
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 25;

  // Sorting
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // Detail editing
  const [editStatus, setEditStatus] = useState('');
  const [editPriority, setEditPriority] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editResolutionNote, setEditResolutionNote] = useState('');
  const [saving, setSaving] = useState(false);

  // Quick actions
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/reports/admin/summary`,
        logout
      );
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Error fetching report summary:', err);
    }
  }, [logout]);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);
      if (filterStatus) params.append('status', filterStatus);
      if (filterPriority) params.append('priority', filterPriority);
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      params.append('sort', sortField);
      params.append('sort_direction', sortDirection);
      params.append('limit', limit);
      params.append('offset', offset);

      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/reports/admin/list?${params.toString()}`,
        logout
      );
      if (response.ok) {
        const data = await response.json();
        setReports(data.reports || []);
        setTotalCount(data.total_count || 0);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterStatus, filterPriority, filterStartDate, filterEndDate, sortField, sortDirection, offset, logout]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setOffset(0);
  };

  const SortArrow = ({ field }) => {
    if (sortField !== field) return <span className="text-gray-300 ml-1">&#8597;</span>;
    return <span className="ml-1">{sortDirection === 'asc' ? '&#9650;' : '&#9660;'}</span>;
  };

  const handleReportClick = async (reportId) => {
    if (selectedReport?.id === reportId) {
      setSelectedReport(null);
      setActionMessage(null);
      return;
    }
    try {
      setDetailLoading(true);
      setActionMessage(null);
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/reports/admin/${reportId}`,
        logout
      );
      if (response.ok) {
        const data = await response.json();
        setSelectedReport(data);
        setEditStatus(data.status);
        setEditPriority(data.priority);
        setEditNotes(data.admin_notes || '');
        setEditResolutionNote(data.resolution_note || '');
      }
    } catch (err) {
      console.error('Error fetching report detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedReport || editStatus === selectedReport.status) return;
    try {
      setSaving(true);
      await authFetch(
        `${process.env.REACT_APP_API_URL}/api/reports/admin/${selectedReport.id}/status`,
        logout,
        { method: 'PATCH', body: JSON.stringify({ status: editStatus }) }
      );
      await handleReportClick(selectedReport.id);
      fetchReports();
      fetchSummary();
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setSaving(false);
    }
  };

  const handlePriorityUpdate = async () => {
    if (!selectedReport || editPriority === selectedReport.priority) return;
    try {
      setSaving(true);
      await authFetch(
        `${process.env.REACT_APP_API_URL}/api/reports/admin/${selectedReport.id}/priority`,
        logout,
        { method: 'PATCH', body: JSON.stringify({ priority: editPriority }) }
      );
      await handleReportClick(selectedReport.id);
      fetchReports();
    } catch (err) {
      console.error('Error updating priority:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleNotesUpdate = async () => {
    if (!selectedReport) return;
    try {
      setSaving(true);
      await authFetch(
        `${process.env.REACT_APP_API_URL}/api/reports/admin/${selectedReport.id}/notes`,
        logout,
        { method: 'PATCH', body: JSON.stringify({ admin_notes: editNotes }) }
      );
      await handleReportClick(selectedReport.id);
    } catch (err) {
      console.error('Error updating notes:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleResolutionNoteUpdate = async () => {
    if (!selectedReport || !editResolutionNote.trim()) return;
    try {
      setSaving(true);
      await authFetch(
        `${process.env.REACT_APP_API_URL}/api/reports/admin/${selectedReport.id}/resolution-note`,
        logout,
        { method: 'PATCH', body: JSON.stringify({ resolution_note: editResolutionNote }) }
      );
      await handleReportClick(selectedReport.id);
    } catch (err) {
      console.error('Error updating resolution note:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleRefundRide = async () => {
    if (!selectedReport?.ride_id) return;
    if (!window.confirm(`Refund ride #${selectedReport.ride_id}? This will issue a full refund to the rider.`)) return;
    try {
      setActionLoading(true);
      setActionMessage(null);
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/rides/${selectedReport.ride_id}/refund`,
        logout,
        { method: 'POST' }
      );
      const data = await response.json();
      if (response.ok) {
        setActionMessage({ type: 'success', text: `Ride #${selectedReport.ride_id} refunded successfully.` });
        await handleReportClick(selectedReport.id);
      } else {
        setActionMessage({ type: 'error', text: data.detail || 'Refund failed.' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Error processing refund.' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefundDayBooking = async () => {
    if (!selectedReport?.day_booking_id) return;
    if (!window.confirm(`Refund day booking #${selectedReport.day_booking_id}? This will issue a full refund to the rider.`)) return;
    try {
      setActionLoading(true);
      setActionMessage(null);
      const response = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/day-bookings/${selectedReport.day_booking_id}/refund`,
        logout,
        { method: 'POST' }
      );
      const data = await response.json();
      if (response.ok) {
        setActionMessage({ type: 'success', text: `Day booking #${selectedReport.day_booking_id} refunded successfully.` });
        await handleReportClick(selectedReport.id);
      } else {
        setActionMessage({ type: 'error', text: data.detail || 'Refund failed.' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Error processing refund.' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivateUser = async () => {
    if (!selectedReport?.reported_user_id) return;
    const userType = selectedReport.reported_user_type;
    const profileId = selectedReport.reported_user_profile_id;
    const userName = selectedReport.reported_user_name || 'this user';

    if (!profileId) {
      setActionMessage({ type: 'error', text: 'Cannot determine user profile ID.' });
      return;
    }

    if (!window.confirm(`Deactivate ${userName}? This will cancel all their future rides and prevent new bookings.`)) return;

    try {
      setActionLoading(true);
      setActionMessage(null);
      const endpoint = userType === 'driver'
        ? `${process.env.REACT_APP_API_URL}/api/admin/drivers/${profileId}/deactivate`
        : `${process.env.REACT_APP_API_URL}/api/admin/riders/${profileId}/deactivate`;

      const response = await authFetch(endpoint, logout, { method: 'POST' });
      const data = await response.json();
      if (response.ok) {
        setActionMessage({ type: 'success', text: `${userName} has been deactivated.` });
      } else {
        setActionMessage({ type: 'error', text: data.detail || 'Deactivation failed.' });
      }
    } catch (err) {
      setActionMessage({ type: 'error', text: 'Error processing deactivation.' });
    } finally {
      setActionLoading(false);
    }
  };

  const resetFilters = () => {
    setFilterCategory('');
    setFilterStatus('');
    setFilterPriority('');
    setFilterStartDate('');
    setFilterEndDate('');
    setOffset(0);
  };

  const totalPages = Math.ceil(totalCount / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div>
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Open</p>
            <p className="text-2xl font-bold text-yellow-600">{summary.by_status?.open || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">In Progress</p>
            <p className="text-2xl font-bold text-blue-600">{summary.by_status?.in_progress || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Resolved</p>
            <p className="text-2xl font-bold text-green-600">{summary.by_status?.resolved || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-500">Critical Open</p>
            <p className="text-2xl font-bold text-red-600">{summary.critical_open || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <select
            value={filterCategory}
            onChange={(e) => { setFilterCategory(e.target.value); setOffset(0); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {CATEGORY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setOffset(0); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={filterPriority}
            onChange={(e) => { setFilterPriority(e.target.value); setOffset(0); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {PRIORITY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <input
            type="date"
            value={filterStartDate}
            onChange={(e) => { setFilterStartDate(e.target.value); setOffset(0); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Start date"
          />
          <input
            type="date"
            value={filterEndDate}
            onChange={(e) => { setFilterEndDate(e.target.value); setOffset(0); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="End date"
          />
          <button
            onClick={resetFilters}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading reports...</div>
        ) : reports.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No reports found</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('priority')}
                    >
                      Priority <SortArrow field="priority" />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reporter</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('created_at')}
                    >
                      Created <SortArrow field="created_at" />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {reports.map(report => (
                    <React.Fragment key={report.id}>
                      <tr
                        onClick={() => handleReportClick(report.id)}
                        className={`cursor-pointer hover:bg-gray-50 transition ${
                          selectedReport?.id === report.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <td className="px-4 py-3 text-sm text-gray-900 font-mono">#{report.id}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                            {CATEGORY_LABELS[report.category] || report.category}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${PRIORITY_STYLES[report.priority] || ''}`}>
                            {report.priority}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">{report.subject}</td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900">{report.reporter_name || 'Unknown'}</div>
                          <div className="text-xs text-gray-500">{report.reporter_type}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${STATUS_STYLES[report.status] || ''}`}>
                            {STATUS_LABELS[report.status] || report.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(report.created_at).toLocaleDateString()}
                        </td>
                      </tr>

                      {/* Expanded Detail Row */}
                      {selectedReport?.id === report.id && (
                        <tr>
                          <td colSpan="7" className="px-0 py-0">
                            {detailLoading ? (
                              <div className="p-6 text-center text-gray-500">Loading details...</div>
                            ) : (
                              <div className="bg-gray-50 border-t border-b border-gray-200 p-6">
                                {/* Action messages */}
                                {actionMessage && (
                                  <div className={`mb-4 p-3 rounded-lg text-sm ${
                                    actionMessage.type === 'success'
                                      ? 'bg-green-50 text-green-800 border border-green-200'
                                      : 'bg-red-50 text-red-800 border border-red-200'
                                  }`}>
                                    {actionMessage.text}
                                  </div>
                                )}

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                  {/* Left: Report Details */}
                                  <div className="space-y-4">
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-500 mb-1">Description</h4>
                                      <p className="text-sm text-gray-900 whitespace-pre-wrap bg-white rounded-lg p-3 border">
                                        {selectedReport.description}
                                      </p>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <h4 className="text-sm font-medium text-gray-500 mb-1">Reporter</h4>
                                        <p className="text-sm text-gray-900">{selectedReport.reporter_name || 'Unknown'}</p>
                                        <p className="text-xs text-gray-500">{selectedReport.reporter_email}</p>
                                        <p className="text-xs text-gray-500 capitalize">{selectedReport.reporter_type}</p>
                                      </div>
                                      {selectedReport.reported_user_id && (
                                        <div>
                                          <h4 className="text-sm font-medium text-gray-500 mb-1">Reported User</h4>
                                          <p className="text-sm text-gray-900">{selectedReport.reported_user_name || 'Unknown'}</p>
                                          <p className="text-xs text-gray-500">{selectedReport.reported_user_email}</p>
                                          <p className="text-xs text-gray-500 capitalize">{selectedReport.reported_user_type}</p>
                                        </div>
                                      )}
                                    </div>

                                    {/* Linked Ride/Booking */}
                                    {selectedReport.ride_details && (
                                      <div>
                                        <h4 className="text-sm font-medium text-gray-500 mb-1">Linked Ride #{selectedReport.ride_id}</h4>
                                        <div className="bg-white rounded-lg p-3 border text-sm">
                                          <p><span className="text-gray-500">Pickup:</span> {selectedReport.ride_details.pickup_address}</p>
                                          <p><span className="text-gray-500">Dropoff:</span> {selectedReport.ride_details.dropoff_address}</p>
                                          <p><span className="text-gray-500">Time:</span> {selectedReport.ride_details.pickup_time ? new Date(selectedReport.ride_details.pickup_time).toLocaleString() : 'N/A'}</p>
                                          <p><span className="text-gray-500">Fare:</span> {selectedReport.ride_details.total_fare ? `$${selectedReport.ride_details.total_fare.toFixed(2)}` : 'N/A'}</p>
                                          <p><span className="text-gray-500">Status:</span> {selectedReport.ride_details.status}</p>
                                        </div>
                                      </div>
                                    )}
                                    {selectedReport.day_booking_details && (
                                      <div>
                                        <h4 className="text-sm font-medium text-gray-500 mb-1">Linked Day Booking #{selectedReport.day_booking_id}</h4>
                                        <div className="bg-white rounded-lg p-3 border text-sm">
                                          <p><span className="text-gray-500">Date:</span> {selectedReport.day_booking_details.booking_date || 'N/A'}</p>
                                          <p><span className="text-gray-500">Type:</span> {selectedReport.day_booking_details.rate_type}</p>
                                          <p><span className="text-gray-500">Fare:</span> {selectedReport.day_booking_details.total_fare ? `$${selectedReport.day_booking_details.total_fare.toFixed(2)}` : 'N/A'}</p>
                                          <p><span className="text-gray-500">Status:</span> {selectedReport.day_booking_details.status}</p>
                                        </div>
                                      </div>
                                    )}

                                    {/* Quick Actions */}
                                    {(selectedReport.ride_id || selectedReport.day_booking_id || selectedReport.reported_user_id) && (
                                      <div>
                                        <h4 className="text-sm font-medium text-gray-500 mb-2">Quick Actions</h4>
                                        <div className="flex flex-wrap gap-2">
                                          {selectedReport.ride_id && (
                                            <button
                                              onClick={handleRefundRide}
                                              disabled={actionLoading}
                                              className="px-3 py-1.5 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 disabled:opacity-50"
                                            >
                                              {actionLoading ? '...' : 'Refund Ride'}
                                            </button>
                                          )}
                                          {selectedReport.day_booking_id && (
                                            <button
                                              onClick={handleRefundDayBooking}
                                              disabled={actionLoading}
                                              className="px-3 py-1.5 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 disabled:opacity-50"
                                            >
                                              {actionLoading ? '...' : 'Refund Day Booking'}
                                            </button>
                                          )}
                                          {selectedReport.reported_user_id && (
                                            <button
                                              onClick={handleDeactivateUser}
                                              disabled={actionLoading}
                                              className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
                                            >
                                              {actionLoading ? '...' : 'Deactivate User'}
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Timestamps */}
                                    <div className="text-xs text-gray-500 space-y-1">
                                      <p>Created: {new Date(selectedReport.created_at).toLocaleString()}</p>
                                      <p>Updated: {new Date(selectedReport.updated_at).toLocaleString()}</p>
                                      {selectedReport.resolved_at && <p>Resolved: {new Date(selectedReport.resolved_at).toLocaleString()}</p>}
                                      {selectedReport.closed_at && <p>Closed: {new Date(selectedReport.closed_at).toLocaleString()}</p>}
                                    </div>
                                  </div>

                                  {/* Right: Admin Actions */}
                                  <div className="space-y-4">
                                    {/* Status Update */}
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-500 mb-1">Update Status</h4>
                                      <div className="flex gap-2">
                                        <select
                                          value={editStatus}
                                          onChange={(e) => setEditStatus(e.target.value)}
                                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                        >
                                          {STATUS_OPTIONS.filter(o => o.value).map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                          ))}
                                        </select>
                                        <button
                                          onClick={handleStatusUpdate}
                                          disabled={saving || editStatus === selectedReport.status}
                                          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          {saving ? '...' : 'Update'}
                                        </button>
                                      </div>
                                    </div>

                                    {/* Priority Update */}
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-500 mb-1">Update Priority</h4>
                                      <div className="flex gap-2">
                                        <select
                                          value={editPriority}
                                          onChange={(e) => setEditPriority(e.target.value)}
                                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                        >
                                          {PRIORITY_OPTIONS.filter(o => o.value).map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                          ))}
                                        </select>
                                        <button
                                          onClick={handlePriorityUpdate}
                                          disabled={saving || editPriority === selectedReport.priority}
                                          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          {saving ? '...' : 'Update'}
                                        </button>
                                      </div>
                                    </div>

                                    {/* Resolution Note (visible to reporter) */}
                                    <div>
                                      <h4 className="text-sm font-medium text-blue-600 mb-1">Resolution Note (visible to reporter)</h4>
                                      <textarea
                                        value={editResolutionNote}
                                        onChange={(e) => setEditResolutionNote(e.target.value)}
                                        rows={3}
                                        maxLength={2000}
                                        className="w-full border border-blue-300 rounded-lg px-3 py-2 text-sm resize-y bg-blue-50"
                                        placeholder="Write a note that will be visible to the person who submitted this report..."
                                      />
                                      <button
                                        onClick={handleResolutionNoteUpdate}
                                        disabled={saving || !editResolutionNote.trim()}
                                        className="mt-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                      >
                                        {saving ? 'Sending...' : 'Send to Reporter'}
                                      </button>
                                    </div>

                                    {/* Admin Notes (internal) */}
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-500 mb-1">Admin Notes (internal only)</h4>
                                      <textarea
                                        value={editNotes}
                                        onChange={(e) => setEditNotes(e.target.value)}
                                        rows={4}
                                        maxLength={5000}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y"
                                        placeholder="Add internal notes about this report..."
                                      />
                                      <button
                                        onClick={handleNotesUpdate}
                                        disabled={saving}
                                        className="mt-2 px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 disabled:opacity-50"
                                      >
                                        {saving ? 'Saving...' : 'Save Notes'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalCount > limit && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing {offset + 1}-{Math.min(offset + limit, totalCount)} of {totalCount}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    disabled={offset === 0}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setOffset(offset + limit)}
                    disabled={offset + limit >= totalCount}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReportsTab;
